import React, { useState } from 'react';
import { Check, Mail, Link2Off, AudioWaveform, FolderSearch, Bookmark, VolumeX, FileWarning } from 'lucide-react';

/**
 * Prepared projects are still being fine-tuned: tell us when one goes wrong. EDITORS, every one of them, new accounts too (Ryan,
 * 2026-10-16: "put a popup for each editor next time they log in with brief reminder about what [prepared projects are] and that we are
 * still working out kinks because there are so many different combos of cameras and such ... implore them to give us
 * feedback when its not working right and give common list of issues ... make them check a box saying they will send me
 * feedback to hello@uncutgems.com").
 *
 * The words are the ones Ryan was given to approve, kept exactly in the constants below so
 * tests/test_prepared_feedback_notice.py can hold them to that, letter for letter.
 *
 * R19: nothing an editor sees may say "auto" or suggest AI. The words here are "prepared project" and "we".
 *
 * One box to tick, at the end of what is read, and the button stays locked until it is: a button with
 * role="checkbox", as in RoughEditStaysAnnouncement (a native checkbox cannot opt out of the global 44px tap-target
 * rule). There is no other way out: no close button, no Escape, no backdrop.
 */

export const FEEDBACK_EMAIL = 'hello@uncutgems.com';
export const BADGE = 'Please read';
export const LABEL = 'Prepared projects';
// Not "learning" (it would hint at a system that learns, R19): fine-tuning is ours to do, by hand.
export const HEADING = [`We're still fine-tuning prepared projects.`, 'Your feedback makes them better.'];
export const INTRO = `For many of your orders, we prepare the project before you start: the footage synced and sorted into bins by camera, the day laid out on a timeline with its key moments marked, and a rough edit to start from. We do this to make your life easier, so you can spend your time editing instead of setting up.`;
export const KINKS = `We are still working out the kinks. Every filmmaker shoots with a different mix of cameras, audio recorders, frame rates and settings, and some combinations still trip us up. We can't catch every one of them without you. When something isn't right, please tell us: every report helps us fix it quickly, for you and for every other editor.`;
export const ISSUES_HEADING = 'Common things to tell us about:';
export const ISSUES = [
  `Clips that won't relink, or that your editing program says are offline or not compatible`,
  'A camera or audio recorder out of sync, or clips placed at the wrong time',
  'Footage missing from the project, or in the wrong bin',
  'Sections or markers in the wrong place (ceremony, speeches, first dance)',
  'Audio that is missing, silent, or on the wrong track',
  `A project that won't open in Premiere Pro, Final Cut Pro or DaVinci Resolve`,
];
// The sentence around the address, which is a mailto: link: "Email <address> with the project name, ..."
export const HOW = ['Email', 'with the project name, your editing program, and what you saw. A screenshot helps a lot.'];
export const AGREE = `I'll email hello@uncutgems.com whenever a prepared project doesn't work right.`;
export const BUTTON = 'Got it';
// The button's words until the box is ticked, as in RoughEditStaysAnnouncement: the box can sit below the fold.
export const LOCKED = 'Tick the box to continue';

const ICONS = [Link2Off, AudioWaveform, FolderSearch, Bookmark, VolumeX, FileWarning];
const TICK_FILL = '#2bb3a8';   // the site's primary button teal
const TICK_INK = '#0a0a0a';

export function PreparedFeedbackAnnouncement({ onAcknowledge }) {
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);

  return (
    <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl bg-ug-raised border border-ug-border shadow-2xl"
         data-testid="whats-new-prepared-feedback">
      <header className="px-6 sm:px-8 pt-8 pb-6">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="font-sans text-[10px] font-semibold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full whitespace-nowrap bg-oxidized-teal/15 text-oxidized-teal border border-oxidized-teal/30"
                data-testid="prepared-feedback-badge">
            {BADGE}
          </span>
          <span className="font-sans text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">{LABEL}</span>
        </div>
        <h2 className="font-heading text-[28px] sm:text-[34px] leading-[1.08] text-zinc-100 mb-3" style={{ textWrap: 'balance' }}
            data-testid="prepared-feedback-heading">
          {HEADING[0]} <span className="text-ug-gold">{HEADING[1]}</span>
        </h2>
        <p className="text-[14px] text-zinc-100/80 leading-relaxed">{INTRO}</p>
        <p className="text-[14px] text-zinc-100/80 leading-relaxed mt-3">{KINKS}</p>
      </header>

      <section className="px-6 sm:px-8 py-6 border-t border-ug-border-subtle" data-testid="prepared-feedback-issues">
        <h3 className="font-heading text-[19px] leading-tight text-zinc-100 mb-3">{ISSUES_HEADING}</h3>
        <ul className="grid gap-2">
          {ISSUES.map((text, i) => {
            const Icon = ICONS[i];
            return (
              <li key={text} className="flex items-start gap-3 rounded-lg bg-ug-surface/50 border border-ug-border px-3.5 py-2.5">
                <Icon className="w-4 h-4 text-oxidized-teal shrink-0 mt-0.5" strokeWidth={2} />
                <span className="text-[13px] text-zinc-100/80 leading-relaxed">{text}</span>
              </li>
            );
          })}
        </ul>
        <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-ug-gold/40 bg-ug-gold/[0.07] px-4 py-3"
             data-testid="prepared-feedback-how">
          <Mail className="w-4 h-4 text-ug-gold shrink-0 mt-0.5" strokeWidth={2} />
          <p className="text-[13px] text-zinc-100 leading-relaxed">
            {HOW[0]}{' '}
            <a href={`mailto:${FEEDBACK_EMAIL}`} data-testid="prepared-feedback-email-link"
               className="tap-auto font-semibold text-ug-gold underline underline-offset-2">{FEEDBACK_EMAIL}</a>
            {' '}{HOW[1]}
          </p>
        </div>
      </section>

      {/* The box to tick, at the end of what is read and on the card's own ground (as in RoughEditStaysAnnouncement:
          the light look paints the footer bar dark, where an empty box is hard to find). */}
      <section className="px-6 sm:px-8 pb-6" data-testid="prepared-feedback-confirm">
        <button type="button" role="checkbox" aria-checked={agreed} onClick={() => setAgreed(v => !v)} data-testid="prepared-feedback-agree"
          className={`tap-auto w-full flex items-start gap-3 text-left rounded-xl px-3.5 py-3 border transition-colors ${
            agreed ? 'border-oxidized-teal/60 bg-oxidized-teal/[0.08]' : 'border-ug-border-strong bg-ug-surface/40 hover:border-ug-gold/50'}`}>
          {/* The filled box in the site's primary teal with black ink, set here so neither theme recolors it. */}
          <span className={`mt-0.5 w-[18px] h-[18px] shrink-0 rounded-md border flex items-center justify-center transition-colors ${
            agreed ? '' : 'border-ug-border-strong'}`} data-testid="prepared-feedback-agree-box"
            style={agreed ? { background: TICK_FILL, borderColor: TICK_FILL } : undefined}>
            {agreed && <Check className="w-3 h-3" style={{ color: TICK_INK }} strokeWidth={3.5} />}
          </span>
          <span className={`text-[12.5px] leading-relaxed ${agreed ? 'text-zinc-100' : 'text-zinc-100/80'}`}>{AGREE}</span>
        </button>
      </section>

      <footer className="sticky bottom-0 px-6 sm:px-8 py-5 border-t border-ug-border-subtle bg-ug-raised/95 backdrop-blur">
        <button type="button" disabled={!agreed || saving} data-testid="whats-new-next"
          onClick={async () => { setSaving(true); try { await onAcknowledge?.(); } finally { setSaving(false); } }}
          className="tap-auto w-full py-3 rounded-lg bg-gold-leaf text-obsidian font-sans text-[11px] font-semibold uppercase tracking-wider transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">
          {saving ? 'Saving…' : agreed ? BUTTON : LOCKED}
        </button>
      </footer>
    </div>
  );
}

export default PreparedFeedbackAnnouncement;
